import React from 'react';
import Icon from '../common/Icons';
import './ConsultationPatientIdentityPremium.css';

function genderLabel(value) {
  if (value === 'M') return 'Homme';
  if (value === 'F') return 'Femme';
  if (value === 'OTHER') return 'Autre';
  return null;
}

function StatTile({ icon, label, value, children, className = '' }) {
  return (
    <div className={`ce-identity-stat ${className}`.trim()}>
      <span className="ce-identity-stat__icon" aria-hidden>
        {icon}
      </span>
      <div className="ce-identity-stat__body">
        <span className="ce-identity-stat__label">{label}</span>
        {children ?? <span className="ce-identity-stat__value">{value || '—'}</span>}
      </div>
    </div>
  );
}

function FooterSep() {
  return (
    <span className="ce-identity-footer__sep" aria-hidden>
      ·
    </span>
  );
}

/**
 * Bandeau identité patient — en-tête ou pied de page consultation.
 */
export default function ConsultationPatientIdentityPremium({
  variant = 'default',
  formData,
  onChange,
  lockedMember = false,
  memberMatricule = '',
  memberCompany = ''
}) {
  const handleChange = (name, value) => {
    if (typeof onChange === 'function') {
      onChange({ target: { name, value } });
    }
  };

  const genderText = genderLabel(formData.patient_gender);
  const ageDisplay = formData.patient_age !== '' && formData.patient_age != null ? `${formData.patient_age} ans` : '—';

  if (variant === 'footer') {
    return (
      <div className="ce-identity-footer" aria-label="Identité patient">
        <Icon name="user" size={15} className="ce-identity-footer__icon" aria-hidden />

        {lockedMember ? (
          <strong className="ce-identity-footer__name">{formData.patient_name || '—'}</strong>
        ) : (
          <input
            type="text"
            name="patient_name"
            className="ce-identity-footer__field ce-identity-footer__field--name"
            value={formData.patient_name}
            onChange={(e) => handleChange('patient_name', e.target.value)}
            placeholder="Nom du patient"
            required
            aria-label="Nom affiché"
          />
        )}

        {(lockedMember || memberMatricule) && (
          <>
            <FooterSep />
            <span>{memberMatricule || '—'}</span>
          </>
        )}

        {(lockedMember || memberCompany) && (
          <>
            <FooterSep />
            <span>{memberCompany || '—'}</span>
          </>
        )}

        <>
          <FooterSep />
          {lockedMember ? (
            <span>{ageDisplay}</span>
          ) : (
            <>
              <input
                type="number"
                name="patient_age"
                className="ce-identity-footer__field ce-identity-footer__field--age"
                value={formData.patient_age}
                onChange={(e) => handleChange('patient_age', e.target.value)}
                min="0"
                max="150"
                placeholder="—"
                aria-label="Âge"
              />
              {formData.patient_age !== '' && formData.patient_age != null ? (
                <span className="ce-identity-footer__suffix">ans</span>
              ) : null}
            </>
          )}
        </>

        <>
          <FooterSep />
          {lockedMember ? (
            <span>{formData.patient_phone || '—'}</span>
          ) : (
            <input
              type="tel"
              name="patient_phone"
              className="ce-identity-footer__field"
              value={formData.patient_phone}
              onChange={(e) => handleChange('patient_phone', e.target.value)}
              placeholder="—"
              aria-label="Téléphone"
            />
          )}
        </>

        <>
          <FooterSep />
          {lockedMember ? (
            <span>{genderText || '—'}</span>
          ) : (
            <select
              name="patient_gender"
              className="ce-identity-footer__field ce-identity-footer__field--gender"
              value={formData.patient_gender}
              onChange={(e) => handleChange('patient_gender', e.target.value)}
              aria-label="Genre"
            >
              <option value="">—</option>
              <option value="M">Homme</option>
              <option value="F">Femme</option>
              <option value="OTHER">Autre</option>
            </select>
          )}
        </>

        {!lockedMember ? <span className="ce-identity-footer__tag">Hors fiche SMIE</span> : null}
      </div>
    );
  }

  return (
    <div className="ce-identity-premium">
      <div className="ce-identity-premium__hero">
        <div className="ce-identity-premium__main">
          <p className="ce-identity-premium__eyebrow">Patient en consultation</p>
          {lockedMember ? (
            <h2 className="ce-identity-premium__name">{formData.patient_name || '—'}</h2>
          ) : (
            <input
              type="text"
              name="patient_name"
              className="ce-identity-premium__name-input"
              value={formData.patient_name}
              onChange={(e) => handleChange('patient_name', e.target.value)}
              placeholder="Nom du patient"
              required
              aria-label="Nom affiché"
            />
          )}

          <div className="ce-identity-premium__chips">
            {memberMatricule ? (
              <span className="ce-identity-chip ce-identity-chip--matricule">
                <Icon name="user" size={12} aria-hidden />
                {memberMatricule}
              </span>
            ) : null}
            {memberCompany ? (
              <span className="ce-identity-chip ce-identity-chip--company">
                <Icon name="building" size={12} aria-hidden />
                {memberCompany}
              </span>
            ) : null}
            {!lockedMember ? (
              <span className="ce-identity-chip ce-identity-chip--manual">Hors fiche SMIE</span>
            ) : null}
          </div>
        </div>
      </div>

      <div className="ce-identity-premium__stats">
        <StatTile icon="📞" label="Téléphone">
          {lockedMember ? (
            <span className="ce-identity-stat__value">{formData.patient_phone || '—'}</span>
          ) : (
            <input
              type="tel"
              name="patient_phone"
              className="ce-identity-stat__input"
              value={formData.patient_phone}
              onChange={(e) => handleChange('patient_phone', e.target.value)}
              placeholder="—"
              aria-label="Téléphone"
            />
          )}
        </StatTile>

        <StatTile icon="🎂" label="Âge">
          {lockedMember ? (
            <span className="ce-identity-stat__value">{ageDisplay}</span>
          ) : (
            <div className="ce-identity-stat__input-wrap">
              <input
                type="number"
                name="patient_age"
                className="ce-identity-stat__input ce-identity-stat__input--short"
                value={formData.patient_age}
                onChange={(e) => handleChange('patient_age', e.target.value)}
                min="0"
                max="150"
                placeholder="—"
                aria-label="Âge"
              />
              {formData.patient_age !== '' && formData.patient_age != null ? (
                <span className="ce-identity-stat__suffix">ans</span>
              ) : null}
            </div>
          )}
        </StatTile>

        <StatTile icon="⚥" label="Genre" className="ce-identity-stat--gender">
          {lockedMember ? (
            <span className="ce-identity-stat__value">{genderText || '—'}</span>
          ) : (
            <div className="ce-identity-gender" role="group" aria-label="Genre">
              {[
                { value: 'M', label: 'H' },
                { value: 'F', label: 'F' },
                { value: 'OTHER', label: '·' }
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={[
                    'ce-identity-gender__btn',
                    formData.patient_gender === opt.value ? 'ce-identity-gender__btn--active' : ''
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => handleChange('patient_gender', opt.value)}
                  aria-pressed={formData.patient_gender === opt.value}
                  title={opt.value === 'M' ? 'Homme' : opt.value === 'F' ? 'Femme' : 'Autre'}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </StatTile>
      </div>
    </div>
  );
}
